import * as React from 'react';

import { Dispatch } from 'redux';
import { connect, useSelector } from 'react-redux';
import AppState from '@webapp/store/state';
import { Translate } from 'react-localize-redux';
import styled from 'styled-components';
import { UpdateArtist } from '@webapp/store/artist/action-creators';
import { RemoveManagerOwnership } from '@webapp/store/ownership/action-creators';
import { CreateInvites } from '../../../store/invites/action-creators';
import { UPDATE_ARTIST } from '@webapp/store/artist/actions';
import SimpleUser from '@common/types/user/simple-user';

import SimpleArtist from '@common/types/artist/simple-artist';

import Box from '@components/layout/box';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';

import InviteTeamMemberList from './invite-team-member-list';
import InvitesListCtrl from '@webapp/controllers/invites/invites-list';
import TeamsListCtrl from '@webapp/controllers/artist/artist-teams-list';
import Hint from '@components/basics/hint';
import OwnershipDistribution from '@components/artist/ownership-distribution';
import RemoveManagerConfirmation from '@components/artist/remove-manager-confirmation';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import SmallNotification from '@components/small-notification';
import { TracInviteTypes } from '@services/invites/types/invite-types';
interface ArtistProfileTeamPageProps {
  className?: string;
  artist?: SimpleArtist;
  isLoading: boolean;
  teamMembers: SimpleUser[];
  updateArtist: (artistData: SimpleArtist) => UpdateArtist;
  removeManagerOwnership: (
    artistId: string,
    trackOwnership: any,
    merchOwnership: any,
    removeOwnership: string
  ) => RemoveManagerOwnership;
  sendInvites: () => CreateInvites;
  listInvites: (artistId: string, type: TracInviteTypes) => void;
  isTablet: boolean;
  isMobile: boolean;
  goToDashboard: () => CallHistoryMethodAction;
  redirectToCatalog: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  artist: state.artist.active,
  teamMembers: state.artist.activeTeam || [],
  isLoading: !!state.ui.loading[UPDATE_ARTIST],
  isTablet: state.ui.tablet,
  isMobile: state.ui.mobile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateArtist: (artistData: SimpleArtist) =>
    dispatch(new UpdateArtist(artistData)),
  sendInvites: () => dispatch(new CreateInvites('team')),
  removeManagerOwnership: (
    artistId: string,
    trackOwnership: any,
    merchOwnership: any,
    removeOwnership: string
  ) => {
    dispatch(
      new RemoveManagerOwnership(
        artistId,
        trackOwnership,
        merchOwnership,
        removeOwnership
      )
    );
  },
  goToDashboard: () => dispatch(push('/dashboard')),
  redirectToCatalog: () => dispatch(push('/catalog')),
});

export const ArtistProfileTeamPage: React.FC<ArtistProfileTeamPageProps> = (
  props
) => {
  const { artist, teamMembers } = props;
  const [completeDistribution, setCompleteDistribution] = React.useState(false);
  const [confirmRemoveManager, SetConfirmRemoveManager] = React.useState(false);
  const [confirmRemoveMember, SetConfirmRemoveMember] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(1);
  const [selectedmember, SetSelectedmember] = React.useState<
    SimpleUser | undefined
  >();
  const [trackData, setTrackData] = React.useState<any>();
  const [artistData, setArtistData] = React.useState(artist);

  const toastNotification = useSelector(
    (state: AppState) => state.ui.toastNotification
  );

  React.useEffect(() => setArtistData(artist), [artist]);
  if (!(artist && artistData)) {
    return null;
  }

  return (
    <>
      {completeDistribution ? (
        <RemoveManagerConfirmation
          username={selectedmember?.fullName}
          redirectToCatalog={props.redirectToCatalog}
        />
      ) : (
        <MotionOpacity className={props.className}>
          {confirmRemoveManager ? (
            <>
              <Typography
                className="mb-20"
                type="subheader"
                customFont="semi-bold"
              >
                <Translate id={'ARTIST_PROFILE.REMOVE_MANAGER.TITLE'} />
              </Typography>
              <Typography
                weight="200"
                type="title"
                opacity=".5"
                className="mt-10 text-align-start w-100 mb-30"
                customFont="light"
                customSize="11px"
              >
                <Translate id="ARTIST_PROFILE.REMOVE_MANAGER.DESCRIPTION" />
              </Typography>
              <Box noBorderBottom>
                <OwnershipDistribution
                  teamMembers={teamMembers}
                  selectedmember={selectedmember}
                  isTablet={props.isTablet}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  trackData={trackData}
                  setTrackData={setTrackData}
                  removeManagerOwnership={props.removeManagerOwnership}
                  artist={artist}
                  goToDashboard={props.goToDashboard}
                  setCompleteDistribution={setCompleteDistribution}
                />
              </Box>
              <Hint noBorderTop noTitle>
                <Translate id="ARTIST_PROFILE.REMOVE_MANAGER.HINT.TITLE" />
              </Hint>
            </>
          ) : (
            <>
              {!props.isMobile ? (
                <Typography
                  className="mb-20"
                  type="subheader"
                  customFont="semi-bold"
                >
                  <Translate id={'ARTIST_PROFILE.TEAM.TITLE'} />
                </Typography>
              ) : null}
              <Box noBorderBottom>
                <InviteTeamMemberList type="team" teamMembers={teamMembers} />
                <InvitesListCtrl type="team" />
                <TeamsListCtrl
                  type="team"
                  teamMembers={teamMembers}
                  confirmRemoveManager={confirmRemoveManager}
                  confirmRemoveMember={confirmRemoveMember}
                  SetConfirmRemoveManager={SetConfirmRemoveManager}
                  SetConfirmRemoveMember={SetConfirmRemoveMember}
                  SetSelectedmember={SetSelectedmember}
                />
              </Box>
              <Hint
                noBorderTop
                customTitle={<Translate id="ARTIST_PROFILE.TEAM.HINT.TITLE" />}
              >
                <Translate id="ARTIST_PROFILE.TEAM.HINT.TEXT" />
              </Hint>
            </>
          )}
          {(toastNotification?.value === 'invite_sent' ||
            toastNotification?.value === 'invite_error') && (
            <SmallNotification
              notificationType={toastNotification.type}
              title={<Translate id={toastNotification.label} />}
            />
          )}
        </MotionOpacity>
      )}
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ArtistProfileTeamPage)`
  margin-bottom: 100px;
  @media ${({ theme }) => theme.device.laptopS} {
    margin-bottom: 0;
  }
`);
